"use client";

import { useState } from "react";

/**
 * A YouTube video that loads only when clicked. Until then it is the video's
 * thumbnail and a play button, so the post doesn't pay for YouTube's player
 * on every page view.
 *
 * The thumbnail is a CSS background, not an <img>: Google picks a result's
 * thumbnail from the biggest <img> on the page, and a video poster there would
 * beat the post's own hero.
 */
export default function YouTubeFacade({ id, title, length }: { id: string; title: string; length?: string }) {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-nws-grey-medium">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
          className="group absolute inset-0 w-full h-full bg-cover bg-center cursor-pointer"
          style={{ backgroundImage: `url(https://i.ytimg.com/vi/${id}/maxresdefault.jpg)` }}
        >
          <span className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/0" />
          {/* Bottom-left, over the logo on our thumbnails: a centred button
              would cover the headline the thumbnail is built around. */}
          <span className="absolute left-3 bottom-3 lg:left-4 lg:bottom-4 flex items-center gap-2 rounded-full bg-black text-white pl-2.5 pr-3.5 py-1.5 lg:pl-3 lg:pr-4 lg:py-2 font-sans text-xs lg:text-sm font-medium shadow-lg transition-transform group-hover:scale-105">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
              <path d="M8 5.5v13l11-6.5z" />
            </svg>
            {length ? `Watch · ${length}` : "Watch"}
          </span>
        </button>
      )}
    </div>
  );
}
